import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { courses } from '../data/courses';
import { destinations } from '../data/destinations';
import { contactTopics, ContactStep, ContactTopicId } from '../data/contactTopics';
import { EnquiryErrors, sendEnquiry, validateEnquiry } from '../utils/enquiry';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface Contact {
  name: string;
  email: string;
  phone: string;
  message: string;
  consent: boolean;
}

const EMPTY_CONTACT: Contact = { name: '', email: '', phone: '', message: '', consent: false };

// State for the dynamic contact page: topic → programme → location → details.
// Accepts ?course=slug, ?location=slug and ?topic=id in the URL to start part-way through.
export function useContactWizard() {
  const [params] = useSearchParams();
  const [topicId, setTopicId] = useState<ContactTopicId | ''>('');
  const [courseSlug, setCourseSlug] = useState('');
  const [locationSlug, setLocationSlug] = useState('');
  const [step, setStep] = useState<ContactStep>('topic');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [contact, setContact] = useState<Contact>(EMPTY_CONTACT);
  const [errors, setErrors] = useState<EnquiryErrors>({});
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    const paramCourse = courses.find((c) => c.slug === params.get('course'));
    const paramLocation = destinations.find((d) => d.slug === params.get('location'));
    const paramTopic = contactTopics.find((t) => t.id === params.get('topic'));
    if (paramCourse) {
      setTopicId(paramCourse.group);
      setCourseSlug(paramCourse.slug);
      const locationOk = paramLocation && paramCourse.locations.includes(paramLocation.slug);
      if (locationOk) setLocationSlug(paramLocation.slug);
      setStep(locationOk || paramCourse.locations.length === 1 ? 'details' : 'location');
      if (!locationOk && paramCourse.locations.length === 1) setLocationSlug(paramCourse.locations[0]);
    } else if (paramTopic) {
      setTopicId(paramTopic.id);
      setStep(paramTopic.steps[1] ?? 'details');
      if (paramLocation) setLocationSlug(paramLocation.slug);
    }
  }, [params]);

  const topic = contactTopics.find((t) => t.id === topicId);
  const course = courses.find((c) => c.slug === courseSlug);
  const location = destinations.find((d) => d.slug === locationSlug);
  const steps: ContactStep[] = topic?.steps ?? ['topic', 'course', 'location', 'details'];
  const stepIndex = steps.indexOf(step);

  const courseOptions = useMemo(() => courses.filter((c) => c.group === topicId), [topicId]);
  const locationOptions = useMemo(() => (course ? destinations.filter((d) => course.locations.includes(d.slug)) : destinations), [course]);

  function chooseTopic(id: ContactTopicId) {
    const next = contactTopics.find((t) => t.id === id);
    setTopicId(id);
    setCourseSlug('');
    setLocationSlug('');
    setAnswers({});
    setStep(next?.steps[1] ?? 'details');
  }

  function chooseCourse(slug: string) {
    const picked = courses.find((c) => c.slug === slug);
    setCourseSlug(slug);
    if (picked && picked.locations.length === 1) {
      setLocationSlug(picked.locations[0]);
      setStep('details');
      return;
    }
    if (picked && locationSlug && !picked.locations.includes(locationSlug)) setLocationSlug('');
    setStep('location');
  }

  function chooseLocation(slug: string) {
    setLocationSlug(slug);
    setStep('details');
  }

  function goTo(target: ContactStep) {
    if (steps.indexOf(target) <= stepIndex) setStep(target);
  }

  function back() {
    if (stepIndex > 0) setStep(steps[stepIndex - 1]);
  }

  function updateContact<K extends keyof Contact>(key: K, value: Contact[K]) {
    setContact((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function answer(id: string, value: string) {
    setAnswers((current) => ({ ...current, [id]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = {
      ...contact,
      course: courseSlug,
      location: locationSlug,
      message: composeMessage(topic?.label ?? '', answers, contact.message),
    };
    const found = validateEnquiry(values);
    setErrors(found);
    const firstError = Object.keys(found)[0];
    if (firstError) {
      event.currentTarget.querySelector<HTMLElement>(`[name="${firstError}"]`)?.focus();
      return;
    }
    setStatus('submitting');
    try {
      await sendEnquiry(values);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  function restart() {
    setTopicId('');
    setCourseSlug('');
    setLocationSlug('');
    setAnswers({});
    setContact(EMPTY_CONTACT);
    setErrors({});
    setStatus('idle');
    setStep('topic');
  }

  return {
    topic, course, location, step, steps, stepIndex, answers, contact, errors, status,
    courseOptions, locationOptions,
    chooseTopic, chooseCourse, chooseLocation, goTo, back, answer, updateContact, submit, restart,
  };
}

function composeMessage(topic: string, answers: Record<string, string>, message: string) {
  const lines = [`Topic: ${topic}`, ...Object.entries(answers).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`)];
  return message.trim() ? `${lines.join('\n')}\n\n${message.trim()}` : lines.join('\n');
}
